import {
  Activity,
  AlertTriangle,
  Lock,
  Power,
  ShieldAlert,
} from "lucide-react";
import { useEffect } from "react";
import { usePsychAssistHeartbeat } from "../../hooks/usePsychAssistHeartbeat";
import { usePsychAssistSession } from "../../hooks/usePsychAssistSession";
import { PsychAssistHeartline } from "./PsychAssistHeartline";
import { FintheonNumericText } from "../numbers/FintheonNumericText";

interface EmotionalResonanceMonitorProps {
  onERScoreChange?: (score: number) => void;
}

const levelTone = {
  unknown: "text-[var(--fintheon-text)]/45",
  steady: "text-[var(--fintheon-accent)]",
  elevated: "text-[var(--fintheon-accent)]/75",
  high: "text-[var(--fintheon-accent)]/65",
  critical: "text-[var(--fintheon-accent)]/55",
};

const levelLabel = {
  unknown: "Unknown",
  steady: "Steady",
  elevated: "Elevated",
  high: "High",
  critical: "Critical",
};

export function EmotionalResonanceMonitor({
  onERScoreChange,
}: EmotionalResonanceMonitorProps) {
  const heartbeat = usePsychAssistHeartbeat();
  const psych = usePsychAssistSession();
  const score = psych.currentScore;
  const level = psych.currentLevel;
  const isActive = psych.isActive;
  const isLockedOut = level === "critical";
  const needsReset = level === "high" || level === "critical";

  useEffect(() => {
    if (score === null) return;
    onERScoreChange?.(score);
  }, [onERScoreChange, score]);

  const toggle = () => {
    if (isActive) void psych.finalize("user_stopped");
    else void psych.start();
  };

  return (
    <div className="rounded border border-[var(--fintheon-accent)]/10 p-2.5 transition-colors duration-200">
      <div className="mb-1.5 flex items-center justify-between">
        <div className="flex items-center gap-1.5">
          <h3 className="text-xs font-semibold text-[var(--fintheon-accent)]">
            PsychAssist
          </h3>
          {psych.retrySeconds > 0 && (
            <span className="rounded bg-[var(--fintheon-accent)]/10 px-1 py-0.5 text-[8px] text-[var(--fintheon-accent)]">
              {psych.retrySeconds}s
            </span>
          )}
        </div>
        <div className="flex items-center gap-1">
          {needsReset && (
            <ShieldAlert className="h-3 w-3 text-[var(--fintheon-accent)]/70" />
          )}
          {isActive ? (
            <Activity className="h-3 w-3 text-[var(--fintheon-accent)]" />
          ) : (
            <Power className="h-3 w-3 text-[var(--fintheon-text)]/40" />
          )}
        </div>
      </div>

      <div className="space-y-1.5">
        <div className="relative h-16 overflow-hidden rounded border border-[var(--fintheon-accent)]/10 bg-black/50">
          <div className="absolute inset-0 scanline-overlay" />
          <PsychAssistHeartline
            status={heartbeat.status}
            loading={heartbeat.loading || psych.syncing}
            isMonitoring={isActive}
            isLockedOut={isLockedOut}
            variant="panel"
          />
        </div>

        <div className="flex items-center justify-between text-[10px]">
          <span className="text-[var(--fintheon-text)]/45">ER Score</span>
          <span className={`font-semibold ${levelTone[level]}`}>
            <FintheonNumericText
              text={String(score === null ? "--" : score.toFixed(1))}
            />{" "}
            / {levelLabel[level]}
          </span>
        </div>

        {psych.recommendation && (
          <div className="rounded border border-zinc-800 p-2 text-[10px] text-[var(--fintheon-text)]/65">
            {needsReset && (
              <AlertTriangle className="mr-1 inline h-3 w-3 text-[var(--fintheon-accent)]/70" />
            )}
            {psych.recommendation}
          </div>
        )}

        {isLockedOut && (
          <div className="rounded border border-[var(--fintheon-accent)]/25 bg-[var(--fintheon-accent)]/10 p-2.5 text-center">
            <Lock className="mx-auto mb-1 h-5 w-5 text-[var(--fintheon-accent)]" />
            <div className="text-[11px] font-bold text-[var(--fintheon-accent)]">
              Skip Session
            </div>
            <div className="mt-0.5 text-[9px] text-[var(--fintheon-text)]/55">
              ER is already critical. Do not start another trade.
            </div>
          </div>
        )}

        {psych.error && (
          <div className="rounded border border-zinc-800 p-1.5 text-[9px] text-[var(--fintheon-text)]/45">
            {psych.error}
          </div>
        )}

        <button
          onClick={toggle}
          className="w-full rounded bg-[var(--fintheon-accent)] px-3 py-1.5 text-[11px] font-semibold text-black transition-opacity hover:opacity-90 disabled:opacity-50"
          disabled={psych.loading}
        >
          {isActive ? "Conclude" : "Initialize"}
        </button>
      </div>
    </div>
  );
}
